/**
 * CR107: Magical UX Layer - Smart Layout Suggestions
 * 
 * Provides helpful tip cards that appear after completing steps
 */

import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lightbulb, X } from "lucide-react";
import { useMagicalUX } from "./MagicalUXProvider";

interface Suggestion {
  id: string;
  trigger: string;
  title: string;
  description: string;
  actionText?: string;
  onAction?: () => void;
}

const SMART_SUGGESTIONS: Suggestion[] = [
  {
    id: 'add-results-after-filters',
    trigger: 'filters-added',
    title: 'Adding a results grid will help users browse faster',
    description: 'Now that you have filters, consider adding a results section to display filtered content.',
    actionText: 'Add Results Grid',
  },
  {
    id: 'highlight-featured-after-results',
    trigger: 'results-added',
    title: 'You may want to highlight featured results',
    description: 'Consider promoting certain results or adding a featured section for better engagement.',
    actionText: 'Add Featured Section',
  },
  {
    id: 'add-search-after-header',
    trigger: 'header-added',
    title: 'A search bar would be perfect here',
    description: 'Help users find what they\'re looking for quickly with a prominent search feature.',
    actionText: 'Add Search Bar',
  },
  {
    id: 'organize-with-tabs',
    trigger: 'layout-complex',
    title: 'Consider organizing content with tabs',
    description: 'Your layout is getting rich with content. Tabs can help organize different sections.',
    actionText: 'Add Tab Navigation',
  },
];

export function SmartSuggestions() {
  const { state, shouldShowSuggestion } = useMagicalUX();
  const [visibleSuggestions, setVisibleSuggestions] = useState<Suggestion[]>([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<string[]>([]);

  useEffect(() => {
    if (!state.isBeginnerMode || !state.lastAction) return;

    const newSuggestions = SMART_SUGGESTIONS.filter(suggestion => 
      suggestion.trigger === state.lastAction &&
      !dismissedSuggestions.includes(suggestion.id) &&
      !visibleSuggestions.find(v => v.id === suggestion.id)
    );

    if (newSuggestions.length > 0) {
      // Show suggestion after a brief delay for better UX
      setTimeout(() => {
        setVisibleSuggestions(prev => [...prev, ...newSuggestions]);
      }, 1500);
    }
  }, [state.lastAction, state.isBeginnerMode, dismissedSuggestions, visibleSuggestions]);

  const dismissSuggestion = (suggestionId: string) => {
    setVisibleSuggestions(prev => prev.filter(s => s.id !== suggestionId));
    setDismissedSuggestions(prev => [...prev, suggestionId]);
  };

  if (!state.isBeginnerMode || visibleSuggestions.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm">
      {visibleSuggestions.map(suggestion => (
        <Card
          key={suggestion.id}
          className="animate-slide-in-right bg-background border-border shadow-lg"
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <Lightbulb className="w-4 h-4 text-muted-foreground" />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground mb-1">
                  💡 Tip: {suggestion.title}
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  {suggestion.description}
                </p>
                
                {suggestion.actionText && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={suggestion.onAction}
                  >
                    {suggestion.actionText}
                  </Button>
                )}
              </div>

              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={() => dismissSuggestion(suggestion.id)}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}